import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  Input,
} from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormGroup,
  FormControl,
} from "@angular/forms";
import {
  CreateCustomerInput,
  DataService,
  Dialog,
  GetCustomerListQuery,
  SharedModule,
  NotificationService,
  LanguageCode,
} from "@bb-vendure/admin-ui/core";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  tap,
} from "rxjs/operators";
import { concat, Observable, of, Subject, take } from "rxjs";
import {
  Idol,
  IdolSectionComponent,
} from "../idol-section/idol-section.component";
import {
  ArtistList,
  CreateEventDocument,
  GetArtistsDocument,
  SortOrder,
} from "src/generated-gql/graphql";

enum ContactInfoType {
  WeChat = "WeChat",
  Line = "Line",
  KakaoTalk = "KakaoTalk",
}

@Component({
  standalone: true,
  imports: [SharedModule, IdolSectionComponent],
  selector: "create-event-dialog",
  templateUrl: "./create-event-dialog.component.html",
  styleUrls: ["./create-event-dialog.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateEventDialogComponent implements OnInit, Dialog<boolean> {
  ngOnInit(): void {}
  resolveWith: (result?: boolean | undefined) => void;
  disableIdol: boolean = false;

  artists$: Observable<any>;
  input$ = new Subject<string>();
  isLoading = false;

  languageCode: LanguageCode = LanguageCode.ko;

  eventInfoForm: FormGroup<{
    name: FormControl<string>;
    type: FormControl<string>;
    artist: FormControl<any>;
    startDate: FormControl<string | null>;
    endDate: FormControl<string | null>;
    applicationStartDate: FormControl<string | null>;
    numsOfWinner: FormControl<number>;
    announcementContent: FormControl<string>;
    allowCorporate: FormControl<boolean>;
  }>;

  idols: Idol[] = [
    {
      name: "",
      numOfWinners: 0,
    },
  ];

  contactTypes = Object.keys(ContactInfoType);
  selectedContactType: ContactInfoType = ContactInfoType.WeChat;

  constructor(
    private dataService: DataService,
    private formBuilder: UntypedFormBuilder,
    private notificationService: NotificationService
  ) {
    this.artists$ = concat(
      this.dataService
        .query(GetArtistsDocument, {
          options: {
            take: 5,
            sort: {
              createdAt: SortOrder.Desc,
            },
          },
        })
        .single$.pipe(
          switchMap((data) => {
            return of(data.artists.items);
          })
        ),
      this.input$.pipe(
        debounceTime(200),
        distinctUntilChanged(),
        tap(() => (this.isLoading = true)),
        switchMap((term) =>
          this.dataService
            .query(GetArtistsDocument, {
              options: {
                filter:
                  term && term.length > 0
                    ? {
                        name: {
                          contains: term,
                        },
                      }
                    : undefined,
                take: 10,
                sort: {
                  createdAt: SortOrder.Desc,
                },
              },
            })
            .mapStream(({ artists }) => artists.items)
            .pipe(
              catchError(() => of([])), // empty list on error
              tap(() => (this.isLoading = false))
            )
        )
      )
    );

    this.eventInfoForm = this.formBuilder.group({
      name: ["", Validators.required],
      type: ["Video Call", Validators.required],
      artist: null,
      startDate: null,
      endDate: null,
      applicationStartDate: null,
      numsOfWinner: [0],
      announcementContent: [""],
    });
  }

  changeIdols(newIdols: Idol[]) {
    this.idols = newIdols;
  }

  save() {
    const data = this.eventInfoForm.value;

    const conditions = [
      { condition: !data.name, message: "Event Name is required" },
      { condition: !data.type, message: "Event Type is required" },
      { condition: !data.artist, message: "Artist is required" },
      { condition: !data.startDate, message: "Start event date is required" },
      { condition: !data.endDate, message: "End event date is required" },
      {
        condition: !data.applicationStartDate,
        message: "Winning result annoucement date is required",
      },
    ];

    for (let { condition, message } of conditions) {
      if (condition) {
        this.notificationService.error(message);
        return;
      }
    }

    if (!this.disableIdol) {
      for (const idol of this.idols) {
        if (!idol.name) {
          this.notificationService.error("Idol name is required");
          return;
        }

        if (idol.numOfWinners < 0) {
          this.notificationService.error(
            "Number of winners per member should more than or equal zero"
          );
          return;
        }
      }
    } else {
      const lessThanZero = {
        condition: data.numsOfWinner! < 0,
        message:
          "Number of winners for all member should more than or equal zero",
      };

      if (lessThanZero.condition) {
        this.notificationService.error(lessThanZero.message);
        return;
      }
    }

    for (let { condition, message } of conditions) {
      if (condition) {
        this.notificationService.error(message);
        return;
      }
    }

    this.dataService
      .mutate(CreateEventDocument, {
        input: {
          translations: [
            {
              languageCode: this.languageCode as any,
              localizedName: data.name ?? "",
              localizedDescription: "",
              officialName: ""
            },
          ],
          name: data.name!,
          artistName: data.artist.id!,
          eventType: data.type!,
          applicationStartDate: new Date(
            data.applicationStartDate!
          ).toISOString(),
          applicationEndDate: new Date().toISOString(),
          numOfWinners: +data.numsOfWinner!,
          startDate: new Date(data.startDate!).toISOString(),
          endDate: new Date(data.endDate!).toISOString(),
          contactInfoType: this.selectedContactType,
          createIdols: this.disableIdol ? undefined : this.idols,
          artistId: data.artist.id!,
          announcementContent: data.announcementContent ?? "",
          allowCorporate: data.allowCorporate ?? false,
        },
      })
      .pipe(take(1))
      .subscribe(({ createEvent }) => {
        if (createEvent.id) {
          this.resolveWith(true);
        }
      });
  }

  cancel() {
    this.resolveWith(false);
  }

  compareFn(item1: { id: string } | null, item2: { id: string } | null) {
    return item1 && item2 ? item1.id === item2.id : item1 === item2;
  }

  trackByFn(item: { id: any }) {
    return item.id;
  }
}
